import React, { useRef, useEffect, useState } from "react";
import { useHome } from "../providers/HomeContext";
import { useCursor } from "../providers/CursorContext";
import { useTheme } from "../providers/ThemeContext";
import { HeroSection } from "./sections/HeroSection";
import { AboutSection } from "./sections/AboutSection";
import { SocialMediaSection } from "./sections/SocialMediaSection";
import { CreditSection } from "./sections/CreditSection";
import { CustomCursor } from "../components/CustomCursor";
import { RippleButton } from "../components/RippleButton";
import { ChevronUp, ChevronDown } from "lucide-react";

import icCircle from "../assets/images/ic_circle.webp";
import icCircleWhite from "../assets/images/ic_circle_white.webp";

interface HomeScreenProps {
  onNavigate: (route: string) => void;
}

export const HomeScreen: React.FC<HomeScreenProps> = ({ onNavigate }) => {
  const { currentPage, setCurrentPage, sectionCount, scrollTarget, setScrollTarget } = useHome();
  const { setCursorSize, setHover } = useCursor();
  const { theme } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile viewports to switch physics
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Smooth scroll helper to jump to a page index
  const scrollToPage = (index: number) => {
    if (isScrollingRef.current) return;
    isScrollingRef.current = true;
    
    setCurrentPage(index);

    if (isMobile && containerRef.current) {
      containerRef.current.scrollTo({
        top: index * window.innerHeight,
        behavior: "smooth",
      });
    }

    // Reset lock after animation duration (380ms)
    setTimeout(() => {
      isScrollingRef.current = false;
    }, 380);
  };

  // Handle desktop mouse wheel scrolls
  useEffect(() => {
    if (isMobile) return; // Native scroll snapping handles this on mobile

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault(); // prevent native browser scrolling
      if (isScrollingRef.current) return;

      // Filter out tiny scrolls to avoid accidental triggers
      if (Math.abs(e.deltaY) < 30) return;

      let targetPage = currentPage;

      if (e.deltaY > 0) {
        if (currentPage < sectionCount - 1) {
          targetPage = currentPage + 1;
        }
      } else {
        if (currentPage > 0) {
          targetPage = currentPage - 1;
        }
      }

      if (targetPage !== currentPage) {
        scrollToPage(targetPage);
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener("wheel", handleWheel, { passive: false });
    }
    return () => {
      if (container) {
        container.removeEventListener("wheel", handleWheel);
      }
    };
  }, [currentPage, isMobile]);

  // Handle mobile native scrolling snap position
  const handleMobileScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!isMobile) return;
    const scrollTop = e.currentTarget.scrollTop;
    const height = window.innerHeight;
    const pageIndex = Math.round(scrollTop / height);
    if (pageIndex !== currentPage && pageIndex >= 0 && pageIndex < sectionCount) {
      setCurrentPage(pageIndex);
    }
  };

  // Listen to programmatic scroll requests
  useEffect(() => {
    if (scrollTarget !== null) {
      scrollToPage(scrollTarget);
      setScrollTarget(null);
    }
  }, [scrollTarget]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown" || e.key === "PageDown") {
        if (currentPage < sectionCount - 1) {
          e.preventDefault();
          scrollToPage(currentPage + 1);
        }
      } else if (e.key === "ArrowUp" || e.key === "PageUp") {
        if (currentPage > 0) {
          e.preventDefault();
          scrollToPage(currentPage - 1);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [currentPage]);

  // Particles generator
  const renderParticles = (whiteTheme: boolean) => {
    const particleImg = (whiteTheme || theme === "dark") ? icCircleWhite : icCircle;
    const opacityClass = theme === "dark" ? "opacity-15 md:opacity-20" : "opacity-50 md:opacity-40";
    const count = isMobile ? 8 : 16;
    // Generate floating bubbles with varying sizing and animation offsets
    return (
      <div className={`absolute inset-0 pointer-events-none overflow-hidden z-0 ${opacityClass}`}>
        {[...Array(count)].map((_, i) => {
          const sizes = [8, 12, 16, 22, 30, 38, 48, 58, 68, 78];
          const size = sizes[i % sizes.length];
          const left = 5 + (i * 7) % 90; // left: 5% to 95%
          const top = 10 + (i * 11) % 80; // top: 10% to 90%
          const animClass = `animate-particle-${(i % 5) + 1}`;

          return (
            <img
              key={i}
              src={particleImg}
              alt=""
              className={`absolute object-contain pointer-events-none select-none origin-center ${animClass}`}
              style={{
                width: size,
                height: size,
                left: `${left}%`,
                top: `${top}%`,
              }}
            />
          );
        })}
      </div>
    );
  };

  return (
    <div className="relative w-screen h-screen overflow-hidden bg-bg transition-colors duration-300 select-none">
      {/* 1. Floating Particle Background (changes to white themed when the social page expands) */}
      {renderParticles(currentPage === 2)}

      {/* 2. Fullscreen slides container using translation transitions on desktop, native scroll snapping on mobile */}
      <div
        ref={containerRef}
        onScroll={handleMobileScroll}
        className="w-full h-full flex flex-col md:transition-transform md:duration-300 md:ease-in-out relative overflow-y-auto md:overflow-y-visible max-md:snap-y max-md:snap-mandatory scroll-smooth"
        style={{
          transform: isMobile ? "none" : `translateY(-${currentPage * 100}vh)`,
        }}
      >
        {/* Section 0: Hero */}
        <div className="w-full h-screen shrink-0 max-md:snap-start max-md:snap-always relative flex items-center justify-center">
          <HeroSection onNavigate={onNavigate} />
        </div>

        {/* Section 1: About */}
        <div className="w-full h-screen shrink-0 max-md:snap-start max-md:snap-always relative flex items-center justify-center">
          <AboutSection />
        </div>

        {/* Section 2: Social Media */}
        <div className="w-full h-screen shrink-0 max-md:snap-start max-md:snap-always relative flex items-center justify-center">
          <SocialMediaSection />
        </div>

        {/* Section 3: Credit/Game */}
        <div className="w-full h-screen shrink-0 max-md:snap-start max-md:snap-always relative flex items-center justify-center">
          <CreditSection />
        </div>
      </div>

      {/* 3. Slide Indicators (Rotating Diamonds on Left) */}
      <div className="absolute left-[3vw] top-[50%] -translate-y-1/2 flex-col gap-6 hidden md:flex z-30">
        {[...Array(sectionCount)].map((_, index) => {
          const isSelected = currentPage === index;
          // Indicators are white on index 2 because the yellow circle expands to cover the screen
          const isYellowBg = currentPage === 2;

          return (
            <button
              key={index}
              onClick={() => scrollToPage(index)}
              onMouseEnter={() => {
                setHover(true);
                setCursorSize(48);
              }}
              onMouseLeave={() => {
                setHover(false);
                setCursorSize(24);
              }}
              className="group focus:outline-none transition-all duration-300 w-4 h-4 flex items-center justify-center relative"
            >
              <div
                className={`w-2.5 h-2.5 transition-all duration-300 border-2 rounded-none ${
                  isSelected
                    ? isYellowBg
                      ? "border-white bg-transparent rotate-[135deg] scale-125"
                      : "border-primary bg-transparent rotate-[135deg] scale-125"
                    : isYellowBg
                    ? "border-white/50 bg-white/50 rotate-45 scale-100"
                    : "border-stroke bg-stroke rotate-45 scale-100"
                }`}
              />
            </button>
          );
        })}
      </div>

      {/* 4. Floating Action Buttons (Up / Down Navigation Arrows on Bottom Right) */}
      <div className="absolute right-[calc(2vw+8px)] bottom-[calc(2vw+4px)] md:right-[3vw] md:bottom-[3vw] hidden md:flex flex-col z-30">
        {/* Scroll Up Button */}
        <RippleButton
          color="bg-primary"
          textColor="text-white dark:text-neutral-900"
          border="border border-white"
          radius="rounded-[6px]"
          padding="p-2 md:p-3"
          onTap={() => {
            if (currentPage > 0) scrollToPage(currentPage - 1);
          }}
          className={`w-9 h-9 md:w-12 md:h-12 transition-all duration-300 flex items-center justify-center ${
            currentPage === 0
              ? "opacity-0 pointer-events-none scale-75"
              : "opacity-100 scale-100"
          }`}
        >
          <ChevronUp className="w-6 h-6 stroke-[2.5px] text-white dark:text-neutral-900" />
        </RippleButton>

        {/* Scroll Down Button */}
        <RippleButton
          color="bg-primary"
          textColor="text-white dark:text-neutral-900"
          border="border border-white"
          radius="rounded-[6px]"
          padding="p-2 md:p-3"
          onTap={() => {
            if (currentPage < sectionCount - 1) scrollToPage(currentPage + 1);
          }}
          className={`w-9 h-9 md:w-12 md:h-12 transition-all duration-300 flex items-center justify-center mt-2 md:mt-3 ${
            currentPage === sectionCount - 1
              ? "opacity-0 pointer-events-none scale-75"
              : "opacity-100 scale-100"
          }`}
        >
          <ChevronDown className="w-6 h-6 stroke-[2.5px] text-white dark:text-neutral-900" />
        </RippleButton>
      </div>

      {/* 5. Custom Cursor Layer */}
      <CustomCursor />
    </div>
  );
};
